import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import moment from 'moment';

import { subDays, format } from 'date-fns';
import { de } from 'date-fns/locale';

import ReactPlayer from 'react-player';

import ProgramPage from '@arte/asg/lib/react/components/Presse/ProgramPagePress';

import Loader from 'Components/Layout/Loader';

import useAPI from 'Hooks/useAPI';
import useMrcAuth from 'Hooks/useMrcAuth';
import useRouter from 'Hooks/useRouter';
import useTranslation from 'Hooks/useTranslation';

import getLang from 'Utils/getLang';

import 'scss/components/placeholder-image.scss';
import 'scss/components/program-page.scss';

const Program = () => {

  const [ downloadLink, setDownloadLink ] = useState(null);
  const [ password, setPassword ] = useState(null);
  const [ program, setProgram ] = useState(null);
  const [ recommendationsZone, setRecommendationsZone ] = useState([]);

  const API = useAPI();
  const authMrc = useMrcAuth();

  const { buildPath } = useRouter();
  const lang = getLang();
  const { date, programId } = useParams();
  const history = useNavigate();
  const { t } = useTranslation();

  const { highlightId, recommendations } = program || {};
  const programImages = Object.values((program || {}).programImages || {});


  // Fetch program data from API, redirect to 404 if not found
  useEffect(() => {
    API.fetchProgram(date, programId)
      .then(data => {
        if (Array.isArray(data)) {
          if (!data.length) {
            history.replace('/404');
            return;
          }

          setProgram(data[0]);
        }
      });
  }, [ API, date, history, programId ]);

  // Only logged in users have access to download links
  useEffect(() => {
    let linkUrl = {};

    if( authMrc.loginState ) {
      linkUrl = buildPath('download', {
        id: programId,
        type: 'program',
        token: authMrc.token
      });
    } else {
      linkUrl = authMrc.loginURL;
    }

    setDownloadLink(linkUrl);
  }, [ authMrc.loginState, authMrc.loginURL, authMrc.token, buildPath, programId ]);


  // Logged in users have access to passwords for external vimeo players
  useEffect(() => {
    if ( authMrc.loginState && highlightId) {
      API.fetchExternalVideoPassword(highlightId)
        .then(pwd => {
          if (pwd && pwd[0]) {
            setPassword(pwd[0]);
          }
        });
    }
  }, [ API, authMrc.loginState, highlightId ]);

  useEffect(() => {

    /**
     * If program is part of an RC, show related programs from same RC
     *
     * Note:
     * for available_on ( 'Online verfügbar am %{date} ), it uses availability.start ( date ), that comes from catchupRightsBegin
     * for broadcast_on ( 'TV-Ausstrahlung am %{date}' ). it uses broadcastDate ( string ) from broadcastBeginRounded
     */
    if ( recommendations && ! highlightId ) {
      const newRecommendations = recommendations
        .filter((item, idx) => {
          // filter out the same program
          for (let i = 0; i < idx; i++) {
            if (recommendations[i].program.id === item.program.id) {
              return false;
            }
          }
          return true;
        })
        .map(item => ({
          ...item.program,

          labels: {
            availability: t('availableOnline'),
            nextBroadcast: t('availableOnline'),
          },
          availability: {
            start: item.catchupRightsBegin,
            type: 'PRESSE',
          },
          broadcastDate: item.broadcastDate,
          beginsAt: item.catchupRightsBegin ? `Online verfügbar ab ${moment(item.catchupRightsBegin).format("DD.MM.")}` : '',
          broadcastAt: item.broadcastDate ? `TV-Ausstrahlung am ${item.broadcastDate}` : '',
          duration: null,
          durationLabel: null,
          images: {
            landscape: {
              resolutions: [
                ...item.program.mainImage.alternateResolutions.map(sizes => ({
                  url: sizes.url,
                  w: sizes.width,
                  h: sizes.height,
                })),
                {
                  url: item.program.mainImage.url,
                  w: item.program.mainImage.width,
                  h: item.program.mainImage.height,
                },
              ],
            },
          },
          kind: {
            code: item.kind,
            isCollection: item.program.programId.startsWith('RC-'),
          },
          type: 'teaser',
          url: buildPath('program', {
            date: item.arteSchedulingDay,
            id: item.programId,
          }),
        }));

      setRecommendationsZone(newRecommendations);
    }

    /** Otherwise if it's a highlighted program, show related videos of
     * the same highlight category. ( siblings )
     *
     * Note:
     * for available_on ( 'Online verfügbar am %{date} ), it uses availability.start ( date ), that comes from catchupRightsBegin
     * for broadcast_on ( 'TV-Ausstrahlung am %{date}' ). it uses broadcastDate ( string )
     */
    if ( highlightId ) {

      API.fetchSiblingHighlights(highlightId)
        .then(data => {
          if (data && data.items) {
            const newRecommendations = Object.values(data.items[0] || {}).map(item => ({
              ...item[0],
              availability: {
                start: item[0].catchupRightsBegin,
                type: 'PRESSE',
              },
              beginsAt: item[0].catchupRightsBegin? `Online verfügbar ab ${moment(item[0].catchupRightsBegin).format("DD.MM.")}` : '',
              broadcastAt: item[0].broadcastDate ? `TV-Ausstrahlung am ${item[0].broadcastDate}` : '',
              duration: null,
              durationLabel: null,
              images: {
                landscape: {
                  resolutions: (item[0].images || [])
                    .filter(sizes => sizes.format === 'landscape')
                    .map(sizes => ({
                      url: sizes.url,
                      w: sizes.width,
                      h: sizes.height,
                    })),
                },
              },
              kind: {
                code: item[0].kind,
                isCollection: item[0].programId.startsWith('RC-'),
              },
              type: 'teaser',
            }));

            setRecommendationsZone(newRecommendations);
          }
        });
    }
  }, [ API, buildPath, highlightId, recommendations, t ] );

  // No program data yet, show loading screen
  if (!program) {
    return (
      <Loader />
    );
  }

  const referrer = document.referrer;
  const { calendarUrl } = window.appConfig;
  let from = referrer || calendarUrl + date + '/' ;
  const currentUrl = window.location.href;

  const onReturnClick = () => {
    if ( from === currentUrl ) {

      // in case its the same, maybe there was some redirection,
      // so its better to rely on browser history.
      history( -1 );
    }

    window.location.href = from;
  }

  // "Previous" link should take user to the calendar page of the given date
  const breadcrumb = {
    previous: {
      label: t('backHighlight'),
      onClick: onReturnClick,
    },
  };

  // Contact section
  const contact = {
    link: program.pdfLink,
    mail: program.pressContact && program.pressContact.email,
    phoneNumber: program.pressContact && program.pressContact.phone,
    name: program.pressContact && program.pressContact.title,
  };

  const credits = (program.crew || [])
    .reduce((acc, { activity, activityCode, name }) => ({
      ...acc,
      [activityCode]: {
        label: activity,
        values: ((acc[activityCode] && acc[activityCode].values) || []).concat([ name ]),
      },
    }), {});

  // short description
  const shortDescription = program.shortPressTextRich ?? ''

  // full description
  const fullDescriptionRich = program.fullDescriptionRich ?? '';
  const additionalPressTextRich = program.additionalPressTextRich ?? '';
  const adicionalText = '<div class="rich-text presse margin-top-s">' + additionalPressTextRich + '</div>';
  const fullDescription = fullDescriptionRich + adicionalText + '';

  const programData = {
    credits: Object.keys(credits).map(code => ({
      code,
      label: credits[code].label,
      values: credits[code].values,
    })),
    fullDescription: fullDescription,
    id: program.id,
    player: program.player,
    shortDescription: shortDescription,
    subtitle: program.subtitle,
    title: program.title,
    broadcastProgrammings: program.broadcastProgrammings,
    genrePresse: program.genrePresse
  };

  programData.player.html = decodeURIComponent(programData.player.html);

  if (program.genrePresse) {
    programData.credits.push({
      code: 'GENRE',
      label: t('genre'),
      values: program.genrePresse,
    });
  }


  if (program.mainBroadcastProgramming.originDisplay) {
    programData.credits.push({
      code: 'ORIGIN',
      label: t('origin'),
      values: program.mainBroadcastProgramming.originDisplay,
    });
  }

  if (program.productionCountryDisplay) {
    programData.credits.push({
      code: 'COUNTRY',
      label: t('country'),
      values: program.productionCountryDisplay,
    });
  }

  if (program.productionYear) {
    programData.credits.push({
      code: 'PRODUCTION_YEAR',
      label: t('year'),
      values: [ program.productionYear.toString() ],
    });
  }

  if (program.castingDisplay) {
    programData.credits = programData.credits.concat(
      Object.keys(program.castingDisplay).map(code => ({
        code,
        label: t('casting'),
        values: program.castingDisplay[code],
      })),
    );
  }

  // "Zones" defines the several different areas of the page.
  const page = {
    zones: [
      // Main content for the program page
      {
        code: {
          id: programId,
          name: 'program_content',
        },
        data: [ programData ],
      },

      // Recommended videos in the right sidebar
      {
        code: {
          id: programId,
          name: 'program_recommendations',
        },
        data: recommendationsZone,
      },

      // We're usurping the most viewed section to show the download images
      // section
      {
        code: {
          id: programId,
          name: 'program_categoryMostViewed',
        },
        data: programImages.length
          ? programImages.map(item => ({
            images: {
              landscape: {
                resolutions: item.alternateResolutions.map(image => ({
                  url: image.url,
                  w: image.width,
                  h: image.height,
                })),
              },
            },
            subtitle: item.caption,
            title: item.copyright,
            type: 'teaser',
            url: !authMrc.loginState
              ? authMrc.loginURL
              : buildPath('download', {
                id: `${item.programId}/${item.imageId}`,
                type: 'file',
                token: authMrc.token
              }),
          }))
          : {},
        oldTitle : t('images'),
        title: ( <a href={ downloadLink }>{ t('images') }</a> ),
      },
    ],
  };

  const broadcastDate = new Date(program.mainBroadcastProgramming.broadcastBegin);
  const broadcastDateFull = broadcastDate.toLocaleDateString(lang, { dateStyle: 'full' });

  var bp_t_h = broadcastDate.getHours();
  const pad_h = ( bp_t_h < 10) ? '0' : '';
  bp_t_h = pad_h + bp_t_h;

  var bp_t_m = broadcastDate.getMinutes();
  const pad_m = ( bp_t_m < 10) ? '0' : '';
  bp_t_m = pad_m + bp_t_m;

  const broadcastTimeShort = bp_t_h + '.' + bp_t_m;

  const programMetas = {

    durationLabel: `${program.mainBroadcastProgramming.durationRounded / 60} ${t('minutesSmall')}`,
    icons: Object.values(program.icons).map(item => ({
      icon: item.icon.class,
      title: item.label,
    })),
    labels: {
      availability: t('availableOnline'),
      nextBroadcast: '',
    },
    nextBroadcast: `${broadcastDateFull} ${t('around')} ${broadcastTimeShort} ${t('hour')}`,
    hasVideoIsLoggedOut: !! ( program.videos && program.videos.length ) && !authMrc.loginState,
    accountLoginURL: authMrc.loginURL,

  };
  programMetas.availability = null;
  if (program.onlineDate) {
    const availabilityBegin = new Date(program.onlineDate);
    const availabilityBeginLong = availabilityBegin.toLocaleDateString(lang, { dateStyle: 'long' });
    programMetas.labels = {
      availability: `${t('broadcastAvailableOnline')} `,
      nextBroadcast: '',
    };
    programMetas.availability = `${t('broadcastAvailableSmallFrom')} ${availabilityBeginLong}`;
  }
  else if (program.videoRightsBegin && program.videoRightsEnd) {
    const availabilityBegin = new Date(program.videoRightsBegin);
    const availabilityBeginLong = availabilityBegin.toLocaleDateString(lang, { dateStyle: 'long' });

    const availabilityEndLong = format( subDays( new Date( program.videoRightsEnd ), 1 ), "dd. LLLL yyyy", {
      locale: de} );
    programMetas.labels = {
      availability: `${t('broadcastAvailableOnline')} `,
      nextBroadcast: '',
    };
    programMetas.availability = `${t('broadcastAvailableSmallFrom')} ${availabilityBeginLong} ${t('to')} ${availabilityEndLong}`;
  }
  else if (
    program.mainBroadcastProgramming
    && program.mainBroadcastProgramming.catchupRightsBegin
    && program.mainBroadcastProgramming.catchupRightsEnd
  ) {
    const availabilityBegin = new Date(program.mainBroadcastProgramming.catchupRightsBegin);
    const availabilityBeginLong = availabilityBegin.toLocaleDateString(lang, { dateStyle: 'long' });

    programMetas.labels = {
      availability: `${t('broadcastAvailableOnline')} `,
      nextBroadcast: '',
    };
    const availabilityEndLong = format( subDays( new Date( program.mainBroadcastProgramming.catchupRightsEnd ), 1 ), "dd. LLLL yyyy", {
      locale: de} );

    programMetas.availability = `${t('broadcastAvailableSmallFrom')} ${availabilityBeginLong} ${t('to')} ${availabilityEndLong}`;
  }

  // Some programs show an external vimeo player rather than an internal embed
  const externalVideo = (program.externalVideo || [])[0] && (
    <ReactPlayer
      url={ `https://vimeo.com/${program.externalVideo[0]}` }
      width="100%"
    />
  );

  // The playerElement is either the external video player, or an image
  // placeholder when the user isn't logged in. This will be shown instead of
  // the internal embed.
  const playerElement = externalVideo || ( ( ! ( program.videos && program.videos.length ) || !authMrc.loginState ) && (
    <img
      alt={ programData.title }
      className="program__placeholder-image"
      src={ program.image && (program.image.large || program.image.full) }
    />
  ));



  const programDataFormatted = {
    ...programData,
    broadcastProgrammings: program.broadcastProgrammings.map( broadcast => {
      const broadcast_d    = new Date( broadcast.broadcastBeginRounded );
      const broadcast_date = broadcast_d.toLocaleDateString( lang, { dateStyle: 'full' } );

      var bp_t_h = broadcast_d.getHours();
      const pad_h = ( bp_t_h < 10) ? '0' : '';
      bp_t_h = pad_h + bp_t_h;

      var bp_t_m = broadcast_d.getMinutes();
      const pad_m = ( bp_t_m < 10) ? '0' : '';
      bp_t_m = pad_m + bp_t_m;

      const broadcast_time = bp_t_h + '.' + bp_t_m;

      const newbroadcastProgramming = {
        ...broadcast,
        broadcastBeginRounded: `${broadcast_date} ${t('around')} ${broadcast_time} ${t('hour')}`
      };
      return newbroadcastProgramming;
    }),
  }

  console.debug({
    breadcrumb, contact, page, password, playerElement, program, programData, programMetas, recommendations
  });


  return (
    <ProgramPage
      breadcrumb={ breadcrumb }
      contact={ contact }
      page={ page }
      password={ password }
      playerElement={ playerElement || null }
      program={ programDataFormatted }
      programMetas={ programMetas }
    />
  );
};

export default Program;
